import { Slider as FluentSlider } from "@fluentui/web-components";
/**
 * Slider
 * @summary A Slider Custom HTML Element based on Fluent UI's Slider with custom styling and behavior.
 *
 * @example
 * ```html
 * <fabric-slider min="0" max="10" step="1" value="5" orientation="horizontal">
 *   <div slot="thumb" class="custom-thumb"></div>
 * </fabric-slider>
 * ```
 *
 * @attr {SliderSize | undefined} size - The size of the slider.
 * @attr {string} value - The initial value of the input.
 * @attr {boolean} disabled - The element's disabled state.
 * @attr {number} min - The minimum allowed value.
 * @attr {number} max - The maximum allowed value.
 * @attr {number} step - Value to increment or decrement via arrow keys, mouse click or drag.
 * @attr {SliderOrientation | undefined} orientation - The orientation of the slider.
 * @attr {SliderMode | undefined} mode - The selection mode.
 *
 * @prop {SliderSize | undefined} size - The size of the slider.
 * @prop {string} value - The initial value of the input.
 * @prop {boolean} disabled - The element's disabled state.
 * @prop {number} min - The minimum allowed value.
 * @prop {number} max - The maximum allowed value.
 * @prop {number} step - Value to increment or decrement via arrow keys, mouse click or drag.
 * @prop {SliderOrientation | undefined} orientation - The orientation of the slider.
 * @prop {SliderMode | undefined} mode - The selection mode.
 *
 * @slot thumb - The slot for a custom thumb element.
 *
 * @csspart track-container - The container element of the track.
 * @csspart thumb-container - The container element of the thumb.
 *
 * @cssprop --border-radius - Sets the border radius for the track and thumb.
 * @cssprop --rail-color - Sets the color of the unfilled rail.
 * @cssprop --slider-direction - Defines the direction used to calculate the track styling.
 * @cssprop --slider-progress - Represents the current progress for styling the track.
 * @cssprop --slider-thumb - Defines the thumb styling for the slider.
 * @cssprop --step-marker-inset - Offsets step markers relative to the track.
 * @cssprop --step-rate - Defines the spacing rate for step markers.
 * @cssprop --thumb-size - Sets the size of the slider thumb.
 * @cssprop --track-color - Sets the color of the filled track.
 * @cssprop --track-margin-inline - Sets the inline margin for the track.
 * @cssprop --track-overhang - Controls how far the track extends past the thumb.
 * @cssprop --track-size - Sets the thickness of the track.
 *
 * @method handleChange - Handles changes to the slider's value.
 * @method handleStepStyles - Handles changes to step styling based on the step value.
 * @method checkValidity - Checks the element's validity.
 * @method reportValidity - Reports the element's validity.
 * @method setCustomValidity - Sets a custom validity message.
 * @method directionChanged - Called when the direction changes.
 * @method increment - Increment the value by the step.
 * @method decrement - Decrement the value by the step.
 * @method handleKeydown - Handles keydown events for the slider.
 * @method setSliderPosition - Places the thumb based on the current value.
 * @method updateStepMultiplier - Update the step multiplier used to ensure rounding errors from steps that are not whole numbers.
 * @method convertToConstrainedValue - Converts a given value to a constrained value within the slider's min and max.
 * @method setDisabledSideEffect - Makes sure the side effects are set up when the disabled state changes.
 *
 * @fires change - Fires a custom 'change' event when the value changes.
 *
 * @extends FluentSlider
 * @tagname fabric-slider
 * @public
 */
export class Slider extends FluentSlider {
}
//# sourceMappingURL=slider.js.map